import React, { useState, useRef, useCallback, useEffect } from "react";
import './Signup.css';
import { motion } from "framer-motion";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate, Link, useLocation } from "react-router-dom";
import Logo from '../../assets/Images/Logo.png';
import { useTranslation } from 'react-i18next';
import Navbar from "../../Components/Navbar";
import api from "../../api/axiosConfig";


const Signup = () => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false); 
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const googleEmail = location.state?.googleEmail;
  const processedRef = useRef(false);
  const mountRef = useRef(false);

  const { t } = useTranslation('Signup');

  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "846376387992-4nh4a7cs4195n25ok2alcfk0293j19ue.apps.googleusercontent.com";
    

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePassword = (password) => {
    return /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
  
    if (!validateEmail(email)) {
      setError(t('signup.validation.email'));
      setLoading(false);
      return;
    }
  
    if (!validatePassword(password)) {
      setError(t('signup.validation.password'));
      setLoading(false);
      return;
    }
  
    if (password !== confirmPassword) {
      setError(t('signup.validation.passwordMatch'));
      setLoading(false);
      return;
    }
  
    if (!termsAccepted) {
      setError(t('signup.validation.terms'));
      setLoading(false);
      return;
    }

    try {
      await api.get('/get-csrf/');
      const payload = { username, email, password };  
      const response = await api.post('/signup/', payload);

      if (response.status === 201 && response.data.user) {
        localStorage.setItem("user", JSON.stringify(response.data.user));
        localStorage.setItem("Authenticated", "true");
        if (response.data.session_key) {
          localStorage.setItem('session_key', response.data.session_key);
        }

        navigate("/homedash", { 
          replace: true,
          state: { freshSignup: true }
        });
        return;
      } else {
        throw new Error("Signup failed");
      }
    } catch (error) {
      if (error.response) {
        const errorMessage = error.response.data?.error || `Server error (${error.response.status})`;
        setError(errorMessage);
      } else if (error.request) {
        setError("Unable to connect to server. Please check your connection.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getRedirectUri = () => {
    return `${window.location.origin}/signup`;
  };

const handleOAuthCallback = useCallback(async (code, state) => {
  setGoogleLoading(true);
  setError(null);

  const savedState = localStorage.getItem('oauth_state');
  if (savedState && state !== savedState) {
    console.error("State mismatch in OAuth callback");
    setError("Security error: Invalid OAuth state. Please try again.");
    setGoogleLoading(false);
    localStorage.removeItem('oauth_state');
    return;
  }
  localStorage.removeItem('oauth_state');

  try {
    await api.get('/get-csrf/'); 
    const redirectUri = getRedirectUri();

    const response = await api.post('/auth/google/', {
      code,
      redirect_uri: redirectUri,
      client_id: clientId,
      state,
      is_signup: true,
    });

    if (response.status === 200 && response.data.success && response.data.user) {
      localStorage.setItem("user", JSON.stringify(response.data.user));
      localStorage.setItem("Authenticated", "true");
      if (response.data.session_key) {
        localStorage.setItem('session_key', response.data.session_key);
      }
      if (response.data.google_access_token) {
        localStorage.setItem('google_access_token', response.data.google_access_token);
      }
      if (response.data.google_refresh_token) {
        localStorage.setItem('google_refresh_token', response.data.google_refresh_token);
      }
      window.history.replaceState({}, document.title, '/signup');

      setTimeout(() => {
        navigate("/homedash", { replace: true, state: { freshSignup: true } });
      }, 300);
    } else {
      throw new Error(response.data.error || "Invalid response from auth server");
    }
  } catch (err) {
    console.error("Google OAuth error:", err);
    if (err.response?.data?.error?.includes("sign up")) {
      setError("Account not found. Creating your account...");
    } else {
      setError(err.response?.data?.error || "Google sign-up failed. Please try again.");
    }
  } finally {
    setGoogleLoading(false);
    setIsProcessing(false);
    processedRef.current = false;
  }
}, [clientId, navigate]);


useEffect(() => {
  if (mountRef.current) return;
  mountRef.current = true;

  if (processedRef.current || isProcessing) return;

  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  const error = urlParams.get('error');

  if (error) {
    setError(`Google sign-up failed: ${error}`);
    window.history.replaceState({}, document.title, '/signup');
    return;
  }

  if (code && !processedRef.current) {
    processedRef.current = true;
    setIsProcessing(true);
    handleOAuthCallback(code, state);
  }
}, [handleOAuthCallback, isProcessing]);

useEffect(() => {
  if (googleEmail) {
    setEmail(googleEmail);
  }
}, [googleEmail]);

const handleGoogleSignup = () => {
  if (!clientId) {
    setError("Google Client ID not configured.");
    return;
  }

  setGoogleLoading(true);
  setError(null);

  try {
    const state = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('oauth_state', state);

    const redirectUri = getRedirectUri();

    const googleAuthUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');

    const params = {
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',        
      scope: 'openid email profile',
      state: state,
      prompt: 'select_account'
    };

    Object.keys(params).forEach(key => 
      googleAuthUrl.searchParams.append(key, params[key])
    );

    window.location.href = googleAuthUrl.toString();
  } catch (err) {
    console.error("Failed to initiate Google signup:", err);
    setError("Failed to start Google sign-up. Please try again.");
    setGoogleLoading(false);
  }
};

return (
  <div className="flex justify-center items-center bg-[var(--bg-black)] from-purple-900 via-black to-purple-900">
    <div className="absolute top-0 left-0 w-full">
        <Navbar minimal />
    </div>
    
    <div className="bg-[var(--bg-black)] border border-[var(--border)] border-solid p-8 rounded-2xl shadow-xl w-96 text-[var(--text-black)] text-center mt-24">
      <div className="flex justify-center bg-transparent">
        <img src={Logo} alt="Logo" className="h-12 w-12 bg-transparent" />                  
      </div>

      <h2 className="text-2xl font-bold mt-4 bg-[var(--bg-black)] text-[var(--text-black)]">{t('signup.title')}</h2>
      <p className="text-gray-400 mt-2 text-sm bg-[var(--bg-black)]">
        {t('signup.loginPrompt')}
        <Link to="/login" className="text-pink-500 font-bold cursor-pointer ml-1 no-underline hover:underline bg-transparent">
          {t('signup.loginLink')}
        </Link>
      </p>

      {error && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-[var(--text-black)] mt-2 bg-pink-500 p-2 rounded-lg"
        >
          {error}
        </motion.p>
      )}
      {success && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-green-500 mt-2 bg-green-900 p-2 rounded-lg"
        >
          {success}
        </motion.p>
      )}

      <form onSubmit={handleSubmit} className="mt-6">
        <input
          type="text"
          placeholder={t('signup.form.username')}
          className="w-full px-4 py-2 mb-4 bg-[var(--bg-black)] text-[var(--text-black)] rounded-lg border-[var(--border)] border border-solid focus:outline-none"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          disabled={loading}
          required
        />
        <input
          type="email"
          placeholder={t('signup.form.email')}
          className="w-full px-4 py-2 mb-4 bg-[var(--bg-black)] text-[var(--text-black)] rounded-lg border-[var(--border)] border border-solid focus:outline-none"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          required
        />
        <div className="relative mb-4">
          <input
            type={passwordVisible ? "text" : "password"}
            placeholder={t('signup.form.password')}
            className="w-full px-4 py-2 mb-4 bg-[var(--bg-black)] text-[var(--text-black)] rounded-lg border-[var(--border)] border border-solid focus:outline-none"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            required
          />
          <span
            onClick={() => setPasswordVisible(!passwordVisible)} 
            className="absolute right-2 -mt-4 top-1/2 -translate-y-1/2 text-[var(--text-black)] cursor-pointer"
          >
            {passwordVisible ? <FaEyeSlash color="white text-[var(--text-black)]" /> : <FaEye color="white text-[var(--text-black)]" />}
          </span>
        </div>

        <div className="relative mb-4">
          <input
            type={confirmPasswordVisible ? "text" : "password"}
            placeholder={t('signup.form.confirmPassword')}
            className="w-full px-4 py-2 mb-4 bg-[var(--bg-black)] text-[var(--text-black)] rounded-lg border-[var(--border)] border border-solid focus:outline-none"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            required
          />
          <span
            onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
            className="absolute right-2 -mt-4 top-1/2 -translate-y-1/2 text-[var(--text-black)] cursor-pointer"
          >
            {confirmPasswordVisible ? <FaEyeSlash color="white text-[var(--text-black)]" /> : <FaEye color="white text-[var(--text-black)]" />}
          </span>
        </div>

        <div className="flex items-start mb-4">
          <input
            type="checkbox"
            id="terms"
            className="mt-1 mr-2 accent-pink-500"
            checked={termsAccepted}
            onChange={() => setTermsAccepted(!termsAccepted)}
            disabled={loading}
          />
          <label htmlFor="terms" className="text-gray-400 text-sm text-left">
            {t('signup.terms.label')}{" "}
            <a href="/terms-and-privacy" className="text-pink-500 hover:underline">
              {t('signup.terms.termsLink')} & {t('signup.terms.privacyLink')}
            </a>
          </label>
        </div>

        <motion.button
          animate={{
            background: [
              "linear-gradient(90deg, #FF1493, #FF69B4)",
              "linear-gradient(90deg, #FF6347, #FF1493)",
            ],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          className="w-full bg-pink-500 hover:bg-pink-600 text-white py-1 rounded-lg text-lg font-semibold border border-none"
          type="submit"
          disabled={loading}
        >
          {loading ? t('signup.form.submitting') : t('signup.form.submit')}
        </motion.button>
      </form>
      
      <div className="flex items-center mt-4">
      <hr className="flex-grow border-gray-600" />
      <span className="mx-2 text-[var(--text-black)]">{t('signup.divider')}</span>
      <hr className="flex-grow border-[var(--border)]" />
      </div>

      <div className="flex justify-center">
      {googleLoading ? (
        <div className="flex items-center justify-center py-3 w-full bg-gray-900 rounded-lg border border-gray-700">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pink-500"></div>
          <span className="ml-3 text-sm text-gray-300">
            {window.location.search.includes('code') ? t('signup.google.loading.processing') : t('signup.google.loading.redirecting')}
          </span>
        </div>
      ) : (
        <button
          onClick={handleGoogleSignup}
          disabled={loading}
          className="w-full bg-[var(--bg-black)] text-[var(--text-black)] py-3 px-4 rounded-lg font-semibold border border-[var(--border)] flex items-center justify-center space-x-2 transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          <span>{t('signup.google.button')}</span>
        </button>
      )}
      </div>      
    </div>
  </div>
  );
};

export default Signup;